<template>
  <div class="base-slider flex items-center gap-2 w-full">
    <BaseText
      v-if="showEndLabels"
      type="body"
      size="xs"
      class="text-text-normal flex-shrink-0"
    >
      {{ formatValue(min) }}
    </BaseText>

    <div class="relative flex-1 flex items-center h-4">
      <!-- Track -->
      <div class="h-0.5 w-full bg-border-normal rounded-sm relative">
        <!-- Filled portion -->
        <div
          class="absolute top-0 left-0 h-0.5 rounded-sm"
          :style="{ width: percent + '%', backgroundColor: color }"
        />
        <!-- Knob -->
        <div
          class="absolute top-1/2 w-2 h-2 rounded-full bg-white"
          :style="{
            left: percent + '%',
            transform: 'translate(-50%, -50%)',
            border: `2px solid ${color}`
          }"
        />
        <!-- Value bubble -->
        <div
          v-if="showBubble"
          class="absolute rounded-md px-1.5 py-0.5"
          :style="{
            left: percent + '%',
            bottom: '12px',
            transform: 'translateX(-50%)',
            backgroundColor: color
          }"
        >
          <BaseText
            type="label"
            size="sm"
            class="text-white whitespace-nowrap"
          >
            {{ formatValue(value) }}
          </BaseText>
        </div>
      </div>

      <!-- Native range input (transparent overlay handling interaction) -->
      <input
        type="range"
        class="base-slider-input absolute inset-0 w-full h-full m-0 cursor-pointer"
        :class="{ 'cursor-not-allowed': disabled }"
        :min="min"
        :max="max"
        :step="step"
        :value="value"
        :disabled="disabled"
        @input="onInput"
      >
    </div>

    <BaseText
      v-if="showEndLabels"
      type="body"
      size="xs"
      class="text-text-normal flex-shrink-0"
    >
      {{ formatValue(max) }}
    </BaseText>
  </div>
</template>

<script>
import BaseText from './BaseText.vue'

export default {
  name: 'BaseSlider',
  components: {
    BaseText
  },
  props: {
    // Model value
    value: {
      type: Number,
      default: 0
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    step: {
      type: Number,
      default: 1
    },
    // Accent color (CSS color) used for the fill, knob border and value bubble
    color: {
      type: String,
      default: '#1F69FF'
    },
    showBubble: {
      type: Boolean,
      default: true
    },
    showEndLabels: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Formatter applied to the min/max labels and the value bubble
    formatValue: {
      type: Function,
      default: (val) => val
    }
  },
  computed: {
    percent () {
      if (this.max === this.min) return 0
      const clamped = Math.min(Math.max(this.value, this.min), this.max)
      return ((clamped - this.min) / (this.max - this.min)) * 100
    }
  },
  methods: {
    onInput (event) {
      this.$emit('input', Number(event.target.value))
    }
  }
}
</script>

<style scoped>
.base-slider-input {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
}
.base-slider-input:focus {
  outline: none;
}
/* Hide the native thumb so only the custom knob is visible, while keeping the hit area */
.base-slider-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: transparent;
  cursor: pointer;
}
.base-slider-input::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
}
.base-slider-input::-webkit-slider-runnable-track {
  background: transparent;
}
.base-slider-input::-moz-range-track {
  background: transparent;
}
</style>
